import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { callToAddImportLog } from '@/services/excel/apis'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { HELPER_OPTION_TRUNCATED_MESSAGE } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { AuthUtils } from '@/utils/AuthUtils'
import { useCompleteExport, useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'
import { buildErpDocumentSummariesExcelUrl } from '@/services/inventory/stockTransferConfirm/apis'
import {
  useConfirmStockTransfer,
  useDeleteErpDocuments,
  useGetErpDocumentItems,
  useGetErpDocumentSummaries,
  useGetGoodsCodeOptions,
  useGetShipperOptions,
  useGetStyleNumberOptions,
  useSaveErpDocuments,
} from '@/services/inventory/stockTransferConfirm/useStockTransferConfirmQueries'
import { ErpDocumentItem, ErpDocumentSummary, GetErpDocumentsReq, SaveErpDocumentsReq } from '@/services/inventory/stockTransferConfirm/types'
import {
  B2B_CARRIER_OPTIONS,
  DELETE_MAX_DOCUMENTS,
  EMPTY_OPTION_LABEL,
  EXCEL_REASON_MIN_LENGTH,
  EXCEL_ROW_LIMIT,
  MESSAGES,
  TRS_TYPE_OPTIONS,
} from '@/pages/inventory/stockTransferConfirm/constants'
import { formatDate8, formatDateTime14 } from '@/utils/format/legacyDate'
import { DeleteReasonModal } from '@/pages/inventory/stockTransferConfirm/components/DeleteReasonModal'
import StockTransferConfirmPageView, { type ActionCtx, type CellOption, type Option, type Row, type Wiring } from './handoff/page.generated'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/**
 * `unknown` 값 → 화면 문자열. 조회영역·모달 폼 버스는 값을 `unknown` 으로 준다.
 *  `String(v ?? '')` 로 바로 찍으면 배열은 `"a,b"`, 객체는 `"[object Object]"` 가 **조용히** 실린다
 *  (moms-fe #803 Sonar S6551). 배열은 첫 칸을 쓰고(멀티 컨트롤 규약), 그 밖의 객체는 빈 값으로 둔다.
 */
const asText = (v: unknown): string => {
  const x = Array.isArray(v) ? v[0] : v
  return typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''
}

const EMPTY_REQ: GetErpDocumentsReq = {}
/** 조회영역 필드 키(생성 뷰 est01FilterCard) — [조회] 때 이 값들이 메뉴 세션·URL 에 남는다. */
const FILTER_KEYS = ['SPR_NM', 'ORD_NO', 'est01StlNo', 'est01GdsCd'] as const

/**
 * 표 그룹 id — 생성본이 wiring.onSelect 두 번째 인자로 주는 값(handoff/page.generated.tsx 의
 *  `<core.TableSelectionScope><SelBridge scope="…" />` 와 그 앞 노드 주석 `#hd-tableGroup` / `#it-tableGroup`).
 *  데모에서 그룹 노드 id 가 바뀌면 여기도 바꿔야 한다 — 안 바꾸면 선택 해제가 어느 표인지 못 가려
 *  **아무 것도 지우지 않는 쪽**(안전 측)으로 떨어진다(틀린 표를 지우지는 않는다).
 */
const HD_SCOPE = 'hd-tableGroup'
const IT_SCOPE = 'it-tableGroup'

/**
 * 표 달력 셀 값 → BE 날짜. 달력 셀은 `YYYY-MM-DD` 를 돌려주는데 BE 는 `\d{8}`(YYYYMMDD)만 받는다
 *  (StockTransferErpDocumentSaveController `@field:Pattern`). 그대로 보내면 400 이다
 *  — 옛 구현은 커스텀 에디터가 YYYYMMDD 로 주고받아 이 문제가 없었다(2026-09-21 #789 리뷰 Critical).
 */
const toBeDate = (v: string | null | undefined): string => String(v ?? '').replace(/-/g, '')

const extractErrorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data?.meta?.message ||
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  fallback

/**
 * 편집분 모으기 — 표가 준 행 중 **원본과 값이 다른 것만** 담는다.
 *  표는 편집 후 전체 행을 주므로(onRowsChange 계약), 그대로 담으면 안 고친 행까지 dirty 가 된다.
 *  원본에 없는 행(새 행)은 그대로 담는다.
 */
function collectDirty<T extends { id?: unknown }>(prev: Map<number, T>, changed: Record<string, unknown>[], origin: T[], editableKeys: readonly string[]): Map<number, T> {
  const byId = new Map(origin.map((o) => [Number((o as { id?: unknown }).id), o]))
  const next = new Map(prev)
  for (const raw of changed) {
    const row = fromViewRow(raw) as unknown as T
    const id = Number((row as { id?: unknown }).id)
    if (!Number.isFinite(id)) continue
    const base = byId.get(id)
    // 원본이 있으면 값이 실제로 달라졌을 때만 담는다(원본 없음 = 새 행).
    //  비교는 **원본(API 행)의 키**로만 한다 — 표가 돌려준 행에는 배선이 붙인 행 key, toViewRow 의 대문자 사본(ORD_NO·SPRNM…)과
    //  그것을 fromViewRow 가 되돌린 소문자 키(sprnm…)까지 실려 있어, 표 행의 키로 비교하면 늘 '다름' 이 되어 전 행이 dirty 가 된다
    //  (#833 리뷰 재현 테스트로 드러남). 편집한 칸은 UPPER_SNAKE → camelCase 로 되돌아와 원본 키와 짝이 맞는다.
    //  조회 API 는 null 필드를 키째로 빼므로 편집 가능한 키(editableKeys)도 더해 비교한다 — 빈 칸을 채운 편집이 빠졌다(LOGISPRD-14064).
    if (base && [...new Set([...Object.keys(base as Record<string, unknown>), ...editableKeys])]
      .filter((k) => !READONLY_DATETIME_KEYS.has(k))
      .every((k) => {
        if (!(k in (row as Record<string, unknown>))) return true // 표 행에 없는 원본 키(빈 화주코드 등)는 편집이 아니다
        const a = (row as Record<string, unknown>)[k]
        const b = (base as Record<string, unknown>)[k]
        if (EDITABLE_DATE_KEYS.has(k)) return dateNorm(a) === dateNorm(b) // 표는 포맷된 값을 돌려주고 원본은 물리 포맷이다
        return a === b || (a ?? '') === (b ?? '')
      })) { next.delete(id); continue }
    next.set(id, row)
  }
  return next
}

/**
 * 선택 행들이 단일 화주인지 — 확정/삭제 API 가 화주 1개 + 문서번호 목록 구조라 혼합 선택을 선차단한다.
 * 화주가 비어 있는 행(sprNm=null)은 애초에 제외한다(리뷰 피드백 2026-08-05).
 */
const singleShipperOf = (rows: { sprNm: string | null }[]): string | null => {
  const shippers = [...new Set(rows.map((row) => row.sprNm))]
  const [shipper] = shippers
  return shippers.length === 1 && shipper ? shipper : null
}

/** 센터명 칸 ↔ 짝 센터코드 칸(표 키·camelCase 키) — 센터명을 고치면 코드도 같이 바꾼다(withWarehouseCodes · [일괄 입력]) */
const WAREHOUSE_CODE_KEYS = [
  ['SRC_WH_NM', 'SRC_WH_CD', 'srcWhCd'],
  ['DST_WH_NM', 'DST_WH_CD', 'dstWhCd'],
] as const

/**
 * 저장되는 편집 키 — 원본에 키가 없어도(API 가 null 을 뺐다) collectDirty 가 비교한다.
 *  생성본 COLUMNS(HEAD)·COLUMNS2(ITEM) 의 편집 컬럼 중 저장 payload 에 실리는 것만 둔다. 표 행의 키 전체로 넓히지 않는다
 *  — toViewRow 사본이 전 행을 dirty 로 만든다(#833). ITEM 의 plnDate·ordQty 는 toItemPayload 에 없어 뺐다
 *  — 넣으면 빈 칸을 채운 편집이 저장 성공처럼 보이고 값은 버려진다(#865 리뷰).
 */
const HEAD_EDITABLE_KEYS = ['plnDate', 'trsType'] as const
const ITEM_EDITABLE_KEYS = [
  'srcWhNm', 'srcWhArea', 'dstWhNm', 'dstWhArea', 'srcBarcode',
  'trsNm', 'trsZip', 'trsAddr', 'trsAddrBase', 'trsAddrDetail', 'trsPhone', 'b2bCarrier',
] as const
/** 편집 가능한 날짜 키 — 표(datepicker)가 `YYYY-MM-DD` 로 돌려주고 원본은 yyyyMMdd 다. 비교는 표기를 벗겨서. */
const EDITABLE_DATE_KEYS = new Set(['plnDate'])
/**
 * 편집하지 않는 일시 키 — 표시용 포맷(`YYYY-MM-DD HH:mm`)이 초를 버려 원본(yyyyMMddHHmmss)으로 되돌릴 수 없다.
 *  표가 돌려준 행에도 실려 오므로 비교에서 아예 뺀다 — 안 빼면 한 칸만 고쳐도 모든 행이 편집분이 된다(#833 리뷰 Critical).
 */
const READONLY_DATETIME_KEYS = new Set(['insDate', 'updDate'])

/**
 * 날짜를 데모 표기로 — 일자 `2026-07-14`, 일시 `2026-07-13 09:12`(재고이동내역조회 est05 와 같은 포맷터).
 *  표 컬럼(PLN_DATE·INS_DATE·UPD_DATE)은 toViewRow 가 이 값을 복사해 그린다. 포맷 밖 값은 그대로 둔다(디자인 QA 2026-09-29: 원문 14자리가 그대로 보였다).
 *  이동예정일자 칸(datepicker)은 `YYYY-MM-DD` 를 받고 돌려주며, 저장은 toBeDate 가 대시를 벗겨 8자리로 보낸다.
 */
const withDateFormats = (r: Record<string, unknown>): Record<string, unknown> => ({
  ...r,
  ...('plnDate' in r ? { plnDate: formatDate8(r.plnDate) } : {}),
  ...('insDate' in r ? { insDate: formatDateTime14(r.insDate) } : {}),
  ...('updDate' in r ? { updDate: formatDateTime14(r.updDate) } : {}),
})

/** 일자 비교용 — 표기(대시)를 벗겨 8자리로. 표시 포맷 때문에 안 고친 행이 편집분으로 잡히지 않게 한다. */
const dateNorm = (v: unknown): string => (typeof v === 'string' || typeof v === 'number' ? String(v) : '').replace(/[-: ]/g, '')

/**
 * API 행(camelCase) → 표 행(UPPER_SNAKE). 생성 뷰의 컬럼은 화면 설계(데모)의 키를 쓴다 —
 * 변환 없이 넘기면 표가 통째로 빈다. 원본 키도 남긴다(배선은 계속 camelCase 로 읽는다).
 */
const toViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...r }
  for (const [k, v] of Object.entries(r)) {
    out[k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()] = v // totalQty → TOTAL_QTY
    // 밑줄 없는 컬럼도 있다(totalQty → TOTALQTY). 한 벌만 내보내면 그 컬럼이 통째로 빈다(2026-09-20 st02 실측).
    out[k.toUpperCase()] = v
  }
  return out
}

/**
 * 표에 보일 '화주명' — API 의 sprNm 은 화주**코드**다(서비스 주석 "SPR_NM=화주 식별 코드").
 * 옛 그리드는 코드→명 매핑으로 그렸다. 매핑이 없으면(목록 로딩 전·미등록 코드) 코드를 그대로 둔다.
 */
const withShipperName = (row: Record<string, unknown>, nameOf: (code: string) => string): Record<string, unknown> => {
  const code = row.SPR_NM
  return typeof code === 'string' && code ? { ...row, SPR_NM: nameOf(code) } : row
}

/**
 * 표 행(UPPER_SNAKE 편집분) → API 행 키. 뷰의 편집 셀은 대문자 키로 값을 돌려준다.
 *  SPR_NM 은 제외한다 — 표에는 **화주명**(표시용)이 들어 있고 API 가 받는 sprNm 은 화주**코드**다.
 *  되돌리면 화주명이 코드 자리에 실려 다른 화주 조회로 나간다(2026-09-20 이음매 테스트가 잡았다).
 *  코드는 같은 행의 camelCase 키(sprNm)에 그대로 남아 있다.
 */
const VIEW_ONLY_KEYS = new Set(['SPR_NM'])
const fromViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...r }
  for (const [k, v] of Object.entries(r)) {
    if (!/^[A-Z0-9_]+$/.test(k) || VIEW_ONLY_KEYS.has(k)) continue
    const camel = k.toLowerCase().replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase())
    out[camel] = v
  }
  return out
}

/**
 * 품목(ITEM) 행인가 — 문서(HEAD) 행에는 없는 키로 가린다.
 *  뷰 행은 UPPER_SNAKE 와 원본 camelCase 를 둘 다 싣는다(toViewRow) — 어느 쪽으로 와도 잡히게 둘 다 본다.
 */
const isItemRow = (r: unknown): boolean => {
  const o = (r ?? {}) as Record<string, unknown>
  return 'ordOptNo' in o || 'ORD_OPT_NO' in o || 'ORDOPTNO' in o
}

const asOptions = (xs: readonly string[]): CellOption[] => xs.map((v) => ({ value: v, label: v || EMPTY_OPTION_LABEL }))
/** 고정 목록 옵션 — 값이 안 변하므로 모듈에서 한 번만 만든다(옵션 슬롯은 뷰가 **매 렌더** 부른다). */
const TRS_TYPE_CELL_OPTIONS = asOptions(TRS_TYPE_OPTIONS)
const B2B_CARRIER_CELL_OPTIONS = asOptions(B2B_CARRIER_OPTIONS)

/*
 * ── UI 가 달라져 빠진 기능: 삭제이력 ──
 * 옛 화면에는 액션바 [삭제이력] → `RemoveLogsModal`(useGetRemoveLogs)이 있었는데 새 디자인에는
 * 그 버튼도 모달도 없다. 데모가 정본이라 화면에서는 사라진다 — 컴포넌트(`components/RemoveLogsModal`)와
 * 훅은 dev 에 그대로 남아 있으니, 되살릴 때는 **데모에 버튼·모달을 먼저 넣고** 재핸드오프한다.
 */
/**
 * EST04 재고이동확정 — 1.0 EST01 을 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-10670)
 *
 * 겉(조회영역·위아래 표·모달)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고, 이 파일은 **옛 훅·API·검증·저장 규칙을
 * 그대로 두고** 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드). 옛 화면 컴포넌트가 나눠 갖던 상태를 여기로 모았다.
 *
 * ── UI 가 달라지며 생긴 차이 ──
 * · 표 행 키가 UPPER_SNAKE 다(뷰 컬럼 기준) — toViewRow/fromViewRow 가 양방향으로 맞춘다.
 * · **엑셀 업로드가 데모에 새로 생겼다**(dev 엔 없던 기능). ITEM 저장 API 경유로 배선한다(옛 주석의 "저장 API 경유 예정" 그대로).
 * · 삭제이력(옛 RemoveLogsModal)은 데모에 버튼이 없어 이번 UI 에서 빠진다 — 필요하면 데모에 넣고 다시 핸드오프한다.
 * · ITEM 엑셀은 옛 ag-Grid 클라이언트 생성 대신 사유 모달 확인 시 1.0 공용 로그만 남긴다(뷰에 그리드 export 가 없다).
 *   TODO(dev): ITEM 서버 엑셀 API 가 생기면 HEAD 와 같은 경로로 바꾼다.
 */
function StockTransferConfirm() {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  const sessionShipperRef = useRef<string>()
  const canEditShipperRef = useRef<boolean>()
  const shipperNameRef = useRef<string>()
  if (sessionShipperRef.current === undefined) {
    const u = AuthStorage.user.get()
    // 화주 전환(MainHeader onConvert)은 shipper_code 만 바꾸고 SPR_NM 은 로그인 화주로 남는다 — 전환 화주를 먼저 읽는다
    //  (반품 2.0 화면들과 같은 규칙). 전환 전엔 둘이 같다.
    sessionShipperRef.current = String(u.shipperCode || u.SPR_NM || '')
    // 화주전환 가능 내부 계정만 화주를 바꿀 수 있다 — 그 외는 자기 화주 고정(옛 필터 `disabled={!canEditShipper}`).
    canEditShipperRef.current = Boolean(u.is_convertible)
    shipperNameRef.current = String((u as { shipperName?: string }).shipperName ?? '')
  }

  /** 조회영역 값(키 = filterKey) → BE 조회 조건. 옛 toErpDocumentsReq 와 같은 규칙(입력 형태만 다르다). */
  const toReq = useCallback((filters: Record<string, unknown>): GetErpDocumentsReq => {
    const text = (v: unknown) => { const s = String(v ?? '').trim(); return s || undefined }
    // 화주 고정 계정은 조회 조건의 화주도 자기 화주로 강제한다 — 뷰에 잠금(disabled) 슬롯이 없어
    //  조회영역에서 다른 값이 올 수 있다. 옛 화면은 셀렉트가 disabled 라 애초에 못 바꿨다.
    const forced = canEditShipperRef.current ? undefined : sessionShipperRef.current || undefined
    return {
      sprNm: forced ?? text(filters.SPR_NM),
      ordNo: (Array.isArray(filters.ORD_NO) ? filters.ORD_NO : []).map((v) => String(v).trim()).filter(Boolean),
      stlNo: text(filters.est01StlNo),
      gdsCd: text(filters.est01GdsCd),
    }
  }, [])

  /**
   * 남겨 둔 조회 조건(URL) — 조회영역 초기값(filterInitial)과 진입 조회가 같은 값을 쓴다. 비면 첫 진입이다.
   *  화주 고정 계정이면 URL 의 화주를 자기 화주로 덮는다(권한 밖 조회를 URL 로 뚫지 못하게).
   */
  const [restored] = useState(() => {
    // 조회영역 필드만 받는다 — 무관한 쿼리(?foo=bar)만 있는 URL 을 남겨 둔 조건으로 보면 화주 고정 계정은 진입 조회가 돈다.
    const r = Object.fromEntries(FILTER_KEYS.filter((k) => parsedQs[k] != null && parsedQs[k] !== '').map((k) => [k, parsedQs[k]]))
    if (!Object.keys(r).length) return undefined
    // 다건 키는 **1건이면 문자열**로 돌아온다(useParsedQuery 의 qs comma 규약) — 배열로 맞춘다.
    //  그대로 두면 조회에서 `filters.ORD_NO.map` 이 없는 메서드라 화면이 죽는다.
    for (const k of ['ORD_NO'] as const) {
      const v = r[k]
      if (v != null && !Array.isArray(v)) r[k] = [String(v)]
    }
    if (!canEditShipperRef.current && sessionShipperRef.current) r.SPR_NM = sessionShipperRef.current
    return r
  })
  // 남겨 둔 조건이 있으면 그 조건으로 시작한다 — 아래 진입 조회가 이 키로 refetch 한다(LOGISPRD-14198).
  const [queryParams, setQueryParams] = useState<GetErpDocumentsReq>(() => (restored ? toReq(restored) : EMPTY_REQ))
  const [masterRow, setMasterRow] = useState<ErpDocumentSummary | null>(null)
  const masterRowRef = useRef<ErpDocumentSummary | null>(null)
  masterRowRef.current = masterRow
  const [selectedHeads, setSelectedHeads] = useState<ErpDocumentSummary[]>([])
  const [selectedItems, setSelectedItems] = useState<ErpDocumentItem[]>([])
  // 편집분(dirty) — id 로 최신 행을 들고 있는다(옛 화면과 같은 규칙).
  const [dirtyHeads, setDirtyHeads] = useState<Map<number, ErpDocumentSummary>>(new Map())
  const [dirtyItems, setDirtyItems] = useState<Map<number, ErpDocumentItem>>(new Map())

  // 첫 진입엔 조회하지 않는다 — 조회 버튼으로만 실행한다(ST10/ST11 동일). 남겨 둔 조건으로 돌아왔을 때만 아래 진입 조회가 돈다.
  const { summaries, isFetching: isHeadFetching, refetch: refetchSummaries } = useGetErpDocumentSummaries(queryParams)
  const itemParams = useMemo<GetErpDocumentsReq>(
    () => (masterRow ? { sprNm: masterRow.sprNm ?? undefined, ordNo: [masterRow.ordNo] } : EMPTY_REQ),
    [masterRow],
  )
  const { items, isFetching: isItemFetching, refetch: refetchItems } = useGetErpDocumentItems(itemParams)

  const { options: shipperOptions } = useGetShipperOptions()
  // 표의 화주명 컬럼은 코드가 아니라 이름을 보여야 한다 — 셀렉트와 같은 목록을 쓴다(옛 그리드 매핑 그대로).
  const shipperNameByCode = useMemo(() => new Map(shipperOptions.map((o) => [String(o.value), String(o.label)])), [shipperOptions])
  const shipperNameOf = useCallback((code: string) => shipperNameByCode.get(code) ?? code, [shipperNameByCode])
  // 센터명 편집 셀렉트 옵션 — MWMS 관리 센터 목록(get-cutoff-warehouses).
  //  글로벌 센터(JP, module=PANTOS)는 고를 수 없게 뺀다. 이름→코드 맵에는 남긴다 — 이미 JP 인 행의 코드는 맞춰야 한다.
  const { data: warehouses } = useGetCutOffWarehouses()
  const warehouseOptions = useMemo<CellOption[]>(
    () => (warehouses ?? []).filter((w) => w.module === 'MWMS').map((w) => ({ value: w.warehouseName, label: w.warehouseName })),
    [warehouses],
  )
  /*
   * 센터명 → 센터코드. 편집 셀은 **이름만** 바꾸므로 짝 코드를 여기서 같이 잡는다.
   *  옛 구현(components/columnDefinitions.tsx 의 valueSetter)이 하던 일이다(QA 2026-08-04).
   *  안 맞추면 표의 센터코드 칸이 옛 코드를 보이고, ITEM 엑셀(SRC_WH_CD·DST_WH_CD 포함)에
   *  이름/코드 불일치가 그대로 실린다 — 저장 전 편집분을 먼저 내보내기 때문이다(#789 리뷰 2026-09-22).
   *  저장 payload 는 코드를 안 보내므로 BE 저장 자체는 안전하다 — **저장 전 화면·내려받은 파일** 한정이다.
   */
  const warehouseCodeByName = useMemo(
    () => new Map((warehouses ?? []).map((w) => [String(w.warehouseName ?? ''), String(w.warehouseCode ?? '')])),
    [warehouses],
  )
  /** 센터명이 바뀐 행은 짝 센터코드도 같이 갱신한다(대문자 키·camelCase 키 양쪽 — 표와 API 가 서로 다른 표기를 본다). */
  const withWarehouseCodes = useCallback(
    (rows: Record<string, unknown>[]): Record<string, unknown>[] =>
      rows.map((r) => {
        const out = { ...r }
        for (const [nameKey, codeKey, camelCode] of WAREHOUSE_CODE_KEYS) {
          const nm = out[nameKey]
          if (typeof nm !== 'string' || !nm) continue
          const code = warehouseCodeByName.get(nm) ?? ''
          out[codeKey] = code
          out[camelCode] = code
        }
        return out
      }),
    [warehouseCodeByName],
  )

  const saveMutation = useSaveErpDocuments()
  const confirmMutation = useConfirmStockTransfer()
  const deleteMutation = useDeleteErpDocuments()
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '재고이동확정' })
  const completeExport = useCompleteExport('DETAIL')

  // 등급 0 또는 MASTER 만 삭제 가능(1.0 동치).
  const isAuth0 = useMemo(() => AuthUtils.isAuth0(), [])

  const resetGridStates = useCallback(() => {
    setMasterRow(null)
    setSelectedHeads([])
    setSelectedItems([])
    setDirtyHeads(new Map())
    setDirtyItems(new Map())
  }, [])

  /** 확정/삭제 후 재조회 — 문서가 조회 범위를 떠나므로 같은 조건으로 다시 읽고 첫 행을 마스터로 잡는다. */
  /*
   * 재조회 뒤에는 **상단에서 클릭해 둔 문서를 그대로 유지**한다. 종전엔 masterRow 만 data[0] 으로 바꿨는데,
   *  생성 뷰의 강조 상태(stPick)는 옛 문서를 가리킨 채 남아 하단 표가 통째로 비었다(2026-09-21 #789 리뷰).
   *  재조회 결과에 그 문서가 남아 있으면 그대로 두고, 사라졌을 때만 첫 행으로 옮긴다.
   */
  const reloadSummaries = useCallback(() => {
    // 클릭해 둔 문서를 **resetGridStates 전에** 붙든다 — reset 이 masterRow 를 비우면 리렌더로 ref 도 null 이 되어
    //  응답이 올 때쯤엔 참조할 값이 남지 않는다(늘 data[0] 으로 떨어졌다, 2026-09-22 #789 리뷰 Critical).
    const keepOrdNo = masterRowRef.current?.ordNo
    resetGridStates()
    refetchSummaries()
      .then(({ data }) => {
        if (!data?.length) { setMasterRow(null); return }
        // 결과에 그 문서가 남아 있으면 그대로 — 뷰의 강조(stPick)와 어긋나지 않게 한다.
        const keep = keepOrdNo ? data.find((d) => d.ordNo === keepOrdNo) : undefined
        setMasterRow(keep ?? data[0])
      })
      .catch(() => undefined) // 사용자 액션 결과 알럿이 이미 떠 있어 중복 알럿을 피한다
  }, [refetchSummaries, resetGridStates])

  /*
   * 진입 조회 — 남겨 둔 조건으로 돌아오면(탭을 다녀옴) 그 조건으로 한 번 조회한다. 조회영역만 되살고 표가 비던
   *  문제(LOGISPRD-14198). 캐시를 다시 쓰지 않고 새로 읽는다. 조회 버튼과 같이 첫 행을 마스터로 잡는다.
   */
  const didRestoreSearch = useRef(false)
  useEffect(() => {
    if (didRestoreSearch.current) return
    didRestoreSearch.current = true
    // restored 가 아니라 sprNm 으로 가른다 — 화주 없는 조회는 [조회]도 막는다. 화주 고정 계정은 toReq 가 세션 화주로 채운다(세션 화주가 있을 때).
    if (!queryParams.sprNm) return
    refetchSummaries()
      .then(({ data, error }) => {
        if (error) { notice.failure(extractErrorMessage(error, MESSAGES.searchFailed)); return }
        if (data?.length) setMasterRow(data[0])
      })
      .catch(() => undefined)
  }, [queryParams, refetchSummaries])

  /** 삭제 대기 문서 — 확인창을 지난 뒤 사유 모달이 열려 있는 동안의 대상. 비면 모달이 닫힌다. */
  const [deleteTargets, setDeleteTargets] = useState<{ shipper: string; ordNos: string[] }>({ shipper: '', ordNos: [] })

  /** 실제 삭제 — 사유 모달이 검증한 사유와 함께. deleted/skipped 를 구분해 알린다(옛 동작 그대로). */
  const runDelete = useCallback(
    async (reason: string) => {
      const { shipper, ordNos } = deleteTargets
      const documentNumbers = [...new Set(ordNos)]
      try {
        const res = await deleteMutation.mutateAsync({ shipperCode: shipper, documentNumbers, reason })
        // **성공한 뒤에야** 닫는다. 먼저 비우면 요청이 실패해도 모달이 닫혀 사유를 다시 적을 길이 없고,
        //  대상(ordNos)까지 사라져 무엇이 안 지워졌는지도 남지 않는다(2026-09-21 사전 리뷰).
        //  열려 있는 동안 [삭제]는 isDeleting 으로 잠겨 있어 중복 요청은 나가지 않는다.
        setDeleteTargets({ shipper: '', ordNos: [] })
        const deleted = res?.data?.deletedDocumentNumbers ?? []
        const skipped = res?.data?.skippedDocumentNumbers ?? []
        reloadSummaries()
        // 일부가 처리되지 않았으면 부분 실패라 알럿으로, 전부 지워졌으면 성공 토스트로 알린다(안내 기준 A).
        if (skipped.length) notice.failure(`${deleted.length}건 삭제되었습니다.\n처리되지 않은 문서: ${skipped.join(', ')}`)
        else notice.success(`${deleted.length}건 삭제되었습니다.`)
      } catch (e) {
        // 모달은 열어 둔다 — 적어 둔 사유 그대로 다시 시도할 수 있게(닫으면 처음부터).
        notice.failure(extractErrorMessage(e, MESSAGES.deleteFailed))
      }
    },
    [deleteMutation, deleteTargets, reloadSummaries],
  )

  /*
   * 서치헬퍼 후보 — 조건은 **모달 안 [조회]를 누른 시점의 값**이다(옛 StockTransferConfirmFilter 의
   *  isXxxHelperSearched·1.0 헬퍼 동치: 입력만으로는 받지 않는다). 뷰가 [조회]·엔터 때 적용 조건을
   *  `wiring.rows.ROWS3/ROWS4` 의 filters 로 준다(__applied = 눌렀음 · 열 때·[초기화] 때 `{}`).
   *  ⚠️ 그래서 ROWS3/ROWS4 의 filters 는 **화면 조회영역 값이 아니다.** 화주는 화면 조회영역에서 와야 하므로
   *   여전히 ROWS(상단 표)의 filters 에서 읽는다 — 상단 표만 화면 조회영역 값을 받는다.
   */
  const [helperShipperKey, setHelperShipperKey] = useState('')
  const helperShipperKeyRef = useRef(helperShipperKey)
  /** 조회영역에서 오는 것은 화주뿐이다 — 브랜드명 칸은 이 화면 조회영역에 없다(모달 안에만 있다). */
  const syncHelperShipper = useCallback((filters?: Record<string, unknown>) => {
    if (!filters) return
    const next = asText(filters.SPR_NM)
    if (next === helperShipperKeyRef.current) return
    helperShipperKeyRef.current = next
    queueMicrotask(() => setHelperShipperKey(next))
  }, [])
  /** 서치헬퍼 모달 열림 — 뷰가 알려 준다(wiring.onModal). 옛 `isXxxHelperOpen` 게이트 자리. */
  const [helperOpen, setHelperOpen] = useState({ stl: false, gds: false })

  /*
   * 모달 적용 조건 — 뷰가 매 렌더 주는 값을 상태로 올린다. 바뀔 때만, 렌더 중 setState 를 피해 마이크로태스크로
   *  (기준 구현 inventoryStatus 와 같은 방식).
   */
  const [stlQuery, setStlQuery] = useState({ applied: false, brand: '', stlNo: '' })
  const stlQueryRef = useRef(stlQuery)
  const syncStlQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM), stlNo: asText(filters?.STL_NO) }
    const cur = stlQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand && next.stlNo === cur.stlNo) return
    stlQueryRef.current = next
    queueMicrotask(() => setStlQuery(next))
  }, [])
  const [gdsQuery, setGdsQuery] = useState({ applied: false, brand: '', item: '' })
  const gdsQueryRef = useRef(gdsQuery)
  /** 마지막으로 본 적용 조건 객체 — [조회]를 누른 순간을 가린다(같은 값 재조회도 안내). */
  const gdsAppliedObjRef = useRef<unknown>(undefined)
  const syncGdsQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM), item: asText(filters?.GDS_NM) }
    // [조회]마다 뷰가 새 적용 조건 객체를 준다(상태라 다시 그려도 같은 객체) — 같은 값으로 다시 눌러도 다시 안내하려고 객체로 판정한다.
    const pressed = filters !== gdsAppliedObjRef.current && next.applied
    gdsAppliedObjRef.current = filters
    if (pressed) {
      queueMicrotask(() => {
        // 조건 없이 누르면 받지 않는다(아래 enabled) — 말없이 0건이면 고장처럼 보이므로 알린다.
        if (!next.brand && !next.item) notice.invalid(MESSAGES.goodsHelperConditionRequired)
      })
    }
    const cur = gdsQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand && next.item === cur.item) return
    gdsQueryRef.current = next
    queueMicrotask(() => setGdsQuery(next))
  }, [])

  const helperShipper = helperShipperKey || sessionShipperRef.current || ''
  /*
   * 스타일넘버 후보 — 화주+브랜드(부분일치) 스코프로 받는다. 옛 구현도 브랜드 없이 [조회]하면 화주만으로 받았다
   *  — 그래서 조건 없는 [조회]도 막지 않는다. 결과가 1,000행 상한에 닿으면 조건을 좁히라고 알린다.
   *  모달이 열려 있고 [조회]를 눌렀을 때만 받는다.
   */
  const styleHelperEnabled = Boolean(helperShipper) && helperOpen.stl && stlQuery.applied
  const {
    items: styleItems,
    isFetching: isStyleFetching,
    isTruncated: isStyleTruncated,
    dataUpdatedAt: styleUpdatedAt,
  } = useGetStyleNumberOptions(
    { shipperCode: helperShipper, brandName: stlQuery.brand || undefined },
    styleHelperEnabled,
  )
  // 2.0 헬퍼는 1,000행에서 자른다 — 모달 [조회]로 받은 결과가 상한에 닿았을 때 알린다.
  //  게이트를 함께 보는 이유: 모달을 닫으면 적용 조건이 비워져 queryKey 가 캐시된 '조건 없음' 키로 돌아가는데,
  //  v5 는 disabled 여도 캐시 data 를 돌려줘 닫힌 화면에 안내가 뜬다. 다시 열고 [조회]하면 게이트가 켜져 다시 알린다.
  useEffect(() => {
    if (styleHelperEnabled && isStyleTruncated) notice.failure(HELPER_OPTION_TRUNCATED_MESSAGE)
  }, [styleHelperEnabled, isStyleTruncated, styleUpdatedAt])
  /*
   * 스타일넘버 칸은 서버로 보내지 않는다(2.0 style-numbers 의 styleNumber 는 정확일치) —
   *  받은 목록을 클라이언트에서 부분일치(대소문자 무시)로 좁힌다. 비어 있으면 그대로.
   */
  const styleOptions = useMemo(() => {
    const kw = stlQuery.stlNo.toLowerCase()
    return kw ? styleItems.filter((o) => String(o.STL_NO ?? '').toLowerCase().includes(kw)) : styleItems
  }, [styleItems, stlQuery.stlNo])
  /*
   * 상품코드 후보 — goods-codes 는 무페이징 distinct 라 대형 화주를 조건 없이 조회하면 응답이 수십 MB 가
   *  되어 브라우저가 멎는다(옛 구현 주석 · dev 실기동 확인). 그래서 **모달이 열려 있고 [조회]를 눌렀고
   *  브랜드명·상품명 중 하나가 채워졌을 때**만. 켜는 조건은 **실제로 요청에 실리는 값**으로 건다 — 다른 키로 켜면
   *  좁히는 값 없이 전체 distinct 가 나간다(st03 에서 같은 실수를 고쳤다). 둘 다 API 가 받으므로 클라이언트 필터는 없다.
   */
  const { items: goodsOptions, isFetching: isGoodsFetching } = useGetGoodsCodeOptions(
    { shipperCode: helperShipper || undefined, brandName: gdsQuery.brand || undefined, itemName: gdsQuery.item || undefined },
    Boolean(helperShipper) && (Boolean(gdsQuery.brand) || Boolean(gdsQuery.item)) && helperOpen.gds && gdsQuery.applied,
  )

  // 엑셀 사유코드 — 1.0 공용 콤보(DOWNLOAD_CD). 데모 옵션은 화면 설계용 예시라 실제 감사로그 코드와 다르다.
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code: string, idx: number) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  const wiring: Wiring = {
    permission: usePermissionSlot('est04'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { est01HeadTable: coreTableLayoutSlot('EST04_HEAD'), est01ItemTable: coreTableLayoutSlot('EST04_ITEM') },
    rows: {
      /*
       * 행 key = **BE 행 id**. 뷰는 안 실으면 배열 인덱스를 key 로 쓰는데(생성본 TODO), 그러면 마스터 문서를
       *  바꿨을 때 같은 인덱스의 새 품목에 이전 체크가 그대로 옮겨 붙는다(옛 handleMasterChange 의
       *  setSelectedItems([]) 자리). 실 id 를 실으면 없는 id 라 stale 체크가 스스로 풀린다.
       */
      ROWS: (_demo, q) => {
        syncHelperShipper(q.filters)
        return { rows: summaries.map((r) => ({ key: r.id, ...withShipperName(toViewRow(withDateFormats(r as unknown as Record<string, unknown>)), shipperNameOf) })) as unknown as Row[], loading: isHeadFetching }
      },
      // 마스터를 고르기 전에는 빈 목록(옛 화면과 같다 — items 는 문서 기준 조회다).
      ROWS2: () => ({ rows: (masterRow ? items : []).map((r) => ({ key: r.id, ...withShipperName(toViewRow(withDateFormats(r as unknown as Record<string, unknown>)), shipperNameOf) })) as unknown as Row[], loading: isItemFetching }),
      /*
       * 서치헬퍼 후보 표 — 옛 훅이 주는 목록 그대로. 응답 키를 **모달 표 컬럼 키로 바꿔** 넘긴다:
       *  스타일넘버는 이미 대문자(STL_NO·BRD_NM·GDS_CD)이고, 상품코드는 itemCode/brandName/itemName 이라
       *  그대로 넘기면 표가 통째로 비고 고른 값도 빈 문자열로 인풋에 들어간다.
       *  바코드 컬럼은 2.0 헬퍼에 대응 값이 없다(별도 헬퍼 소관) — 빈 칸으로 둔다.
       */
      //  filters = 모달 [조회] 적용 조건(화면 조회영역 아님) — 상태로 올려 위 enabled 게이트에 쓴다.
      ROWS3: (_demo, q) => {
        syncStlQuery(q?.filters)
        return { rows: styleOptions as unknown as Row[], loading: isStyleFetching }
      },
      ROWS4: (_demo, q) => {
        syncGdsQuery(q?.filters)
        return { rows: goodsOptions.map((o) => ({ ...o, BRD_NM: o.brandName ?? '', GDS_CD: o.itemCode ?? '', GDS_NM: o.itemName ?? '' })) as unknown as Row[], loading: isGoodsFetching }
      },
    },
    actions: {
      search: async (filters) => {
        const req = toReq(filters)
        // 세션 화주가 없는 계정은 화주 없이 열린다 — 화주 없는 조회는 막는다(est03·st03 와 같다). 검증 실패 = false(조회 안 함).
        if (!req.sprNm) {
          notice.invalid(MESSAGES.shipperRequired)
          return false
        }
        MenuStorage.menuOnSession.setSearch(filters)
        // queryParams 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다(클릭 문맥).
        flushSync(() => setQueryParams(req))
        resetGridStates()
        menuSession.reload()
        const { data, error } = await refetchSummaries()
        replaceQuery(filters)
        if (error) { notice.failure(extractErrorMessage(error, MESSAGES.searchFailed)); return }
        // 초기 진입 시 첫 행 자동 선택(핸드오프 §테이블 동작 계약) — 디테일이 곧바로 채워진다.
        if (data?.length) setMasterRow(data[0])
      },
      /*
       * 편집 셀 — 표는 **편집 후 전체 행**을 돌려준다(pbo-core Table onRowsChange 계약). 받은 걸 그대로 쌓으면
       *  셀 하나만 고쳐도 그 페이지의 모든 행이 dirty 가 되어, 고치지도 않은 행이 저장 요청에 실린다
       *  ("체크한 행 중 편집된 것만" QA 규칙이 무너진다 — 2026-09-22 #789 리뷰 Critical).
       *  원본과 맞대서 **실제로 값이 달라진 행만** 담는다.
       */
      ROWS_rowsChange: (changed) => {
        setDirtyHeads((prev) => collectDirty(prev, changed, summaries, HEAD_EDITABLE_KEYS))
      },
      ROWS2_rowsChange: (changed) => {
        // 센터명을 고치면 짝 센터코드도 같이 — 옛 valueSetter 규칙(위 withWarehouseCodes 주석).
        const next = collectDirty(dirtyItems, withWarehouseCodes(changed), items, ITEM_EDITABLE_KEYS)
        setDirtyItems(next)
        // 이번 편집으로 값이 새로 바뀐 행 id — 뷰가 체크에 더한다(1.0 편집 시 자동 체크, LOGISPRD-14156).
        //  이미 편집돼 있던 행은 값이 그대로면 빼서, 사용자가 해제한 행이 다른 행 편집으로 다시 체크되지 않게 한다.
        return [...next.keys()].filter((id) => {
          const before = dirtyItems.get(id) as Record<string, unknown> | undefined
          const after = next.get(id) as unknown as Record<string, unknown>
          return !before || ITEM_EDITABLE_KEYS.some((k) => (before[k] ?? '') !== (after[k] ?? ''))
        })
      },
      /**
       * HEAD 저장 — **체크한 행 중 편집된 것만** 보낸다(QA 피드백 2026-08-05). 편집분 전체를 보내면
       * 실패한 행이 dirty 로 남아 이후 저장마다 계속 딸려간다.
       */
      hdSaveButton: async (ctx) => {
        // 선택은 pickHeads 를 거친다 — ctx.selected 를 id 로 직접 맞추면 하단 품목 선택이 섞인다(두 표의 id 가 겹친다).
        const selectedIds = new Set(pickHeads(ctx).map((r) => r.id))
        const rows = [...dirtyHeads.values()].filter((row) => selectedIds.has(row.id))
        if (rows.length === 0) { throw invalidError(MESSAGES.noDirtyRowsInSelection) }
        try {
          await saveMutation.mutateAsync({
            updateSummaries: rows.map((row) => ({
              shipperCode: row.sprNm ?? '',
              documentNumber: row.ordNo,
              plannedDate: toBeDate(row.plnDate),
              transportType: row.trsType ?? '',
            })),
          })
          // 재조회가 표의 미저장 편집까지 서버값으로 덮는다 — 편집분을 남기면 안 보이는 값이 다음 저장에 섞인다.
          setDirtyHeads(new Map())
          // 뷰는 selEpoch 로 체크를 비우지만 **배선의 선택은 남는다** — 그대로 두면 다음 [확정]·[삭제] 가
          //  화면에 없는 옛 선택을 대상으로 잡는다(2026-09-22 #789 리뷰). 여기서 같이 비운다.
          setSelectedHeads([])
          // 품목 저장과 같은 이유로 재조회한다. 조회조건·선택 문서는 그대로 둔다 — 편집 칸(일자·운송구분)은 조회조건·하단 품목 키가 아니다.
          refetchSummaries().then(({ error }) => { if (error) notice.failure(extractErrorMessage(error, MESSAGES.searchFailed)) })
          return MESSAGES.saveDone
        } catch (e) {
          const msg = extractErrorMessage(e, MESSAGES.saveFailed)
          throw failureError(msg)
        }
      },
      /** ITEM 저장 — 편집된 품목의 센터/수령지/운송사 편집분을 반영한다. 범위 규칙은 HEAD 저장과 같다. */
      itSaveButton: async (ctx) => {
        const selectedIds = new Set(pickItems(ctx).map((r) => r.id))
        const rows = [...dirtyItems.values()].filter((row) => selectedIds.has(row.id))
        if (rows.length === 0) { throw invalidError(MESSAGES.noDirtyRowsInSelection) }
        try {
          await saveMutation.mutateAsync({ updateItems: rows.map(toItemPayload) })
          // 재조회가 표의 미저장 편집까지 서버값으로 덮는다 — 편집분을 남기면 안 보이는 값이 다음 저장에 섞인다.
          setDirtyItems(new Map())
          setSelectedItems([]) // 위와 같은 이유 — 뷰만 비워지고 배선 선택이 남으면 다음 액션이 옛 행을 잡는다
          // 표는 dataSource 가 바뀔 때만 다시 그린다 — 캐시 그대로면 저장 전 값이 남는다.
          //  refetch 는 실패해도 reject 하지 않고 error 로 resolve 한다(조회 버튼과 같은 처리).
          refetchItems().then(({ error }) => { if (error) notice.failure(extractErrorMessage(error, MESSAGES.searchFailed)) })
          return MESSAGES.saveDone
        } catch (e) {
          const msg = extractErrorMessage(e, MESSAGES.saveFailed)
          throw failureError(msg)
        }
      },
      /** 확정 — 선택 문서를 MWMS 이동지시로 내려보낸다. 서버가 07:30~08:10 확정을 차단한다. */
      // 확인 슬롯은 **반환값이 없다(void)** — 생성 뷰가 토스트·닫기를 한다. 예전엔 `false` 를 돌려 토스트를
      //  막았는데, 이 화면 모달엔 토스트 문구 자체가 없어 **필요 없는 값**이었다(골격 wiring.skeleton.ts 도
      //  `async () => {}` 를 정본으로 준다). false 를 남겨 두면 생성본이 그 값을 '실패' 로 읽게 바뀌는 순간
      //  모달이 안 닫힌다 — 2026-09-21 에 실제로 그렇게 깨졌다. 실패는 throw 로만 알린다.

      est01ConfirmModal_ok: async (ctx) => {
        const rows = pickHeads(ctx)
        const shipper = singleShipperOf(rows)
        if (shipper === null) { throw invalidError(MESSAGES.mixedShippers) }
        try {
          const res = await confirmMutation.mutateAsync({ shipper, documentNumbers: rows.map((row) => row.ordNo) })
          reloadSummaries() // 확정 문서는 조회 범위를 떠난다 — 같은 조건으로 재조회(조회조건 유지)
          return res?.meta?.message || MESSAGES.confirmDone
        } catch (e) {
          const msg = extractErrorMessage(e, MESSAGES.confirmFailed)
          throw failureError(msg)
        }
      },
      /**
       * 삭제(auth0 전용) — 확인창 [확인] 뒤 **사유 모달**을 연다.
       *
       * ── UI 가 달라져 생긴 차이 ──
       * 새 디자인의 삭제 확인창은 사유 입력이 없는 단순 confirm 이다. 그런데 BE 는 삭제 사유를
       * legacy byte 15~90B(한글 5~30자)로 **강제**한다(DeleteStockTransferDocumentsUsecase) —
       * 빈 사유로 부르면 전건 400 이고, 감사 사유도 사라진다.
       * 그래서 확인창 다음에 개발팀이 만든 사유 모달(DeleteReasonModal, 바이트 선검증 포함)을 그대로 띄운다.
       * TODO(design): 데모 삭제 확인창에 사유 입력이 생기면 이 모달과 아래 렌더를 걷어내고
       *   `ctx.form.reason` 을 받는다(옛 모달의 15~90B 검증은 그때도 남겨야 한다).
       */
      /*
       * 삭제 확인창 — **여기서 지우지 않는다.** 사유 모달(DeleteReasonModal)만 열고, 실제 삭제는 거기 [삭제]가 한다.
       *  그래서 `false` 를 돌려 **확인창의 성공 토스트를 막는다** — void 를 돌리면 생성본이 성공으로 읽어
       *  아직 아무것도 안 지웠는데 "삭제되었습니다" 가 먼저 뜬다(2026-09-21 실측). 이 화면에서 false 가
       *  필요한 유일한 자리다(다른 슬롯은 토스트가 없어 void 가 맞다).
       */
      est01DeleteConfirm_ok: async (ctx): Promise<false> => {
        const rows = pickHeads(ctx)
        const shipper = singleShipperOf(rows)
        if (shipper === null) { throw invalidError(MESSAGES.mixedShippers) }
        setDeleteTargets({ shipper, ordNos: rows.map((row) => row.ordNo) })
        return false
      },
      /** HEAD 엑셀 — 서버 xlsx. 선택 문서(ordNo IN)만 내려받는다. */
      est01HeadDownloadModal_ok: async (ctx): Promise<void> => {
        const { reason, detail } = reasonOf(ctx)
        const rows = pickHeads(ctx)
        onDownloadExcel(
          buildErpDocumentSummariesExcelUrl({
            sprNm: queryParams.sprNm,
            ordNo: [...new Set(rows.map((row) => row.ordNo))],
            detailedReason: detail,
            reasonCode: reason,
          }),
        )
      },
      /**
       * ITEM 엑셀 — ITEM 은 서버 엑셀 API 가 없어 옛 화면처럼 **클라이언트에서 만든다**.
       * 옛 구현은 ag-Grid `exportDataAsExcel({ onlySelected })` 였는데 뷰에는 그리드 export 가 없어
       * 같은 결과(선택 행 · 표에 보이는 컬럼·헤더)를 exceljs 로 직접 만든다. 사유 로그는 공용 useCompleteExport(2.0 엑셀 감사로그).
       * TODO(dev): ITEM 서버 엑셀 API 가 생기면 HEAD 와 같은 경로로 바꾼다.
       */
      est01ItemDownloadModal_ok: async (ctx): Promise<void> => {
        const { reason, detail } = reasonOf(ctx)
        const picked = pickItems(ctx)
        if (!picked.length) { throw invalidError(MESSAGES.noSelection) }
        // 편집분 우선 — 옛 ag-Grid `exportDataAsExcel` 는 **화면의 현재 행 상태**(저장 전 편집 포함)를 내보냈다.
        //  pickItems 는 서버 조회 원본이라 그대로 쓰면 고쳐 놓고 저장 전에 받은 파일에 편집이 빠진다
        //  (2026-09-21 moms-fe #789 리뷰). 저장([저장])과 같은 '선택 ∩ 편집' 규칙으로 맞춘다.
        const rows = picked.map((r) => dirtyItems.get(r.id) ?? r)
        await saveItemsExcel(rows.map((r) => withShipperName(toViewRow(withDateFormats(r as unknown as Record<string, unknown>)), shipperNameOf)))
        completeExport.mutate(`${reason}_${detail}`)
      },
      /**
       * 엑셀 업로드 — 파일의 각 행을 저장 API 로 보낸다. 문서번호가 있으면 ITEM 수정(updateItems),
       * 없으면 신규 생성(createItems, 1.0 EST01 업로드와 같다). 헤더는 한글 표 헤더·UPPER_SNAKE 키 둘 다 받는다.
       */
      est01UploadModal_ok: async (ctx) => {
        const file = ctx.form?.file as File | undefined
        if (!file) { notice.invalid(MESSAGES.uploadNoFile); throw new Error('파일 없음') }
        let rows: Record<string, unknown>[]
        try {
          rows = await readUploadRows(file)
        } catch (e) {
          const msg = e instanceof Error ? e.message : '파일을 읽지 못했습니다.'
          throw failureError(msg)
        }
        if (rows.length === 0) { notice.invalid(MESSAGES.uploadEmpty); throw new Error('빈 파일') }
        /*
         * 화주는 **파일 값을 믿지 않는다** — 다운로드 파일의 화주 칸에는 코드가 아니라 화주'명'이 들어 있고
         *  (withShipperName), 사용자가 고쳐 올릴 수도 있다. 조회한 화주 → 조회영역의 현재 화주 → 세션 화주 순으로 채운다.
         *  [조회] 를 누르지 않아도 올라간다 — 1.0 은 조회 없이 import→저장이 됐는데 2.0 이 조회 커밋값만 보고
         *  막아 REOPEN 됐다(LOGISPRD-14484). 화주 고정 계정은 조회와 같이 세션 화주로 강제한다(toReq).
         *  나머지 필수값(문서번호·순번)이 비면 **보내기 전에** 막고 몇 번째 행인지 알린다 —
         *  빈 값이 BE 로 넘어가면 센터명 등을 '' 로 덮어써 데이터가 망가진다(#789 리뷰 Critical).
         */
        const sprNm = (canEditShipperRef.current
          ? queryParams.sprNm || helperShipperKey || sessionShipperRef.current
          : sessionShipperRef.current) ?? ''
        if (!sprNm) { notice.invalid(MESSAGES.uploadNoShipper); throw new Error('화주 없음') }
        /*
         * 문서번호가 있는 행 = 기존 문서 수정(updateItems), 없는 행 = 신규 센터간이동 생성(createItems — 1.0 EST01 업로드 양식).
         *  생성은 BE 가 요청당 화주 1·이동예정일 1 만 받는다 — 날짜가 섞이면 보내기 전에 막는다(LOGISPRD-14484).
         */
        const updateItems: ReturnType<typeof toItemPayload>[] = []
        const createItems: NonNullable<SaveErpDocumentsReq['createItems']> = []
        for (const r of rows) {
          const item = fromViewRow(r) as unknown as ErpDocumentItem
          const line = Number(r[UPLOAD_LINE_KEY])
          // 문서번호·순번 칸 중 하나라도 값이 있으면 수정 행이다 — 읽지 못한 값(객체 등)을 신규 생성으로 흘리지 않는다(생성은 멱등이 아니다).
          if ([r.ORD_NO, r.ORD_OPT_NO].some((v) => v != null && v !== '')) {
            const p = toItemPayload({ ...item, sprNm } as ErpDocumentItem)
            if (!asText(p.documentNumber) || !asText(p.documentSequence)) throw rowInvalidError(MESSAGES.uploadMissingKey(line))
            updateItems.push(p)
          } else {
            const c = {
              shipperCode: sprNm,
              plannedDate: asText(item.plnDate).replace(/\D/g, ''),
              sourceBarcode: asText(item.srcBarcode),
              sourceWarehouseName: asText(item.srcWhNm),
              destinationWarehouseName: asText(item.dstWhNm),
              orderQuantity: Number(asText(item.ordQty)),
            }
            const ok = /^\d{8}$/.test(c.plannedDate) && c.sourceBarcode && c.sourceWarehouseName && c.destinationWarehouseName
              && Number.isInteger(c.orderQuantity) && c.orderQuantity > 0
            if (!ok) throw rowInvalidError(MESSAGES.uploadCreateInvalid(line))
            createItems.push(c)
          }
        }
        if (new Set(createItems.map((c) => c.plannedDate)).size > 1) throw rowInvalidError(MESSAGES.uploadCreateMixedDate)
        try {
          await saveMutation.mutateAsync({
            ...(createItems.length ? { createItems } : {}),
            ...(updateItems.length ? { updateItems } : {}),
          })
          reloadSummaries()
          // 그리드 없이 바로 저장하는 화면이라 저장이 성공해야 업로드가 반영된다 — 그때 1건 남긴다. menuCode 는 다운로드 로그(useCompleteExport)와 같은 EST04.
          callToAddImportLog({ menuCode: 'EST04', apiName: 'EST04_LIST_IMPORT', rowCount: rows.length })
          return `${rows.length}건 업로드했습니다.`
        } catch (e) {
          const msg = extractErrorMessage(e, MESSAGES.saveFailed)
          throw failureError(msg)
        }
      },
      // 서치헬퍼 [확인] — 고른 값을 인풋에 넣는 일은 뷰가 한다.
      est01StlSearchModal_ok: () => {},
      est01GdsSearchModal_ok: () => {},
    },
    /** 상단에서 **행을 클릭해** 고른 문서로 하단 품목을 읽는다. 문서가 바뀌면 ITEM 선택을 비운다. */
    onPick: (rows) => {
      const r = fromViewRow((rows[0] ?? {}) as Record<string, unknown>) as unknown as ErpDocumentSummary
      if (!rows.length) { setMasterRow(null); return }
      if (r.ordNo !== masterRow?.ordNo) setSelectedItems([])
      setMasterRow(r)
    },
    /**
     * 체크 선택 — 위/아래 표가 각자 알린다. **스코프별로 갈라 둔다**: 뷰의 ctx.selected 는 마지막으로
     * 바뀐 스코프 하나뿐이라, 반대 표를 체크한 뒤 이 표의 버튼을 눌렀을 때 pickHeads/pickItems 가 여기서 되찾는다.
     *
     * scope = 알려 온 표 그룹 id(생성본 SelBridge 가 준다). 선택을 **푼** 신호는 rows 가 비어 있어
     *  행 모양('ordOptNo' 유무)으로는 어느 표인지 가릴 수 없다 — 그때 scope 가 없으면 하단 품목 체크를
     *  풀었을 때 상단 문서 선택이 지워져 [확정]·[삭제]가 대상을 잃는다(2026-09-21 사전 리뷰).
     */
    onSelect: (rows, scope) => {
      const mapped = rows.map((r) => fromViewRow(r as Record<string, unknown>))
      // 행이 있으면 행 모양이 가장 확실하다(옛 생성본은 scope 를 주지 않는다). 비었을 때만 scope 에 기댄다.
      if (!mapped.length) {
        if (scope === IT_SCOPE) setSelectedItems([])
        else if (scope === HD_SCOPE) setSelectedHeads([])
        // 모르는 스코프(옛 생성본·테스트)면 **아무 것도 지우지 않는다** — 틀린 표를 지우는 쪽이 더 나쁘다.
        return
      }
      if (mapped.some((r) => 'ordOptNo' in r)) setSelectedItems(mapped as unknown as ErpDocumentItem[])
      else setSelectedHeads(mapped as unknown as ErpDocumentSummary[])
    },
    /** [일괄 입력] 센터명은 짝 센터코드도 같이 패치한다 — 표의 센터코드 칸이 옛 코드로 남지 않게(LOGISPRD-15530, withWarehouseCodes 와 같은 규칙) */
    bulkInput: {
      ROWS2: (demo) =>
        demo.map((it) => {
          const codeKey = WAREHOUSE_CODE_KEYS.find(([nameKey]) => nameKey === it.key)?.[1]
          return codeKey ? { ...it, toPatch: (v: string) => ({ [it.key]: v, [codeKey]: warehouseCodeByName.get(v) ?? '' }) } : it
        }),
    },
    options: {
      /**
       * 화주 목록 — **화주전환 가능 계정만 전체**를 본다. 그 외는 자기 화주 하나만 남겨 고정한다
       * (옛 필터의 disabled 대체 — 열어도 다른 화주가 없다). 로그인 화주가 목록에 없으면 보강한다.
       * TODO(dev): 셀렉트를 실제로 잠그려면 컨덕터 조회 필드에 disabled 속성이 필요하다(컨덕터 후속).
       */
      EST01SHIPPER_OPTIONS: () => {
        const mine = sessionShipperRef.current ?? ''
        const all = mine && !shipperOptions.some((o) => String(o.value) === mine)
          ? [{ value: mine, label: shipperNameRef.current || mine }, ...shipperOptions]
          : shipperOptions
        return (canEditShipperRef.current ? all : all.filter((o) => String(o.value) === mine)) as Option[]
      },
      /**
       * 엑셀 사유코드(HEAD·ITEM 두 모달) — 1.0 공용 콤보(DOWNLOAD_CD).
       * 데모 옵션(업무활용·고객요청…)은 화면 설계용 예시라 실제 감사로그 코드와 다르다.
       * 콤보가 비면(로딩·실패) 빈 목록을 준다 — 없는 코드를 고르게 두는 쪽이 더 나쁘다.
       */
      REASONRADIO_OPTIONS: () => reasonOptions as Option[],
      REASONRADIO_OPTIONS2: () => reasonOptions as Option[],
      ROWS_TRS_TYPE: () => TRS_TYPE_CELL_OPTIONS,
      ROWS2_SRC_WH_NM: () => warehouseOptions,
      ROWS2_DST_WH_NM: () => warehouseOptions,
      ROWS2_B2B_CARRIER: () => B2B_CARRIER_CELL_OPTIONS,
    },
    filterDefaults: {
      est01FilterCard: (demo) => ({ ...demo, SPR_NM: sessionShipperRef.current || undefined }),
    },
    /** 진입 초기값 — 남겨 둔 조회 조건(위 restored)이 기본값을 덮는다. */
    filterInitial: {
      est01FilterCard: (defaults) => ({ ...defaults, ...restored }),
    },
    useBusy: () => saveMutation.isPending || confirmMutation.isPending || deleteMutation.isPending || isDownloading,
    opens: {
      /** 확정 — 선택이 있어야 하고 화주가 섞이면 안 된다(옛 handleConfirm 선검증). */
      hdConfirmButton: (ctx) => guardHeads(ctx),
      hdExcelButton: (ctx) => {
        const rows = pickHeads(ctx)
        if (rows.length === 0) { notice.invalid(MESSAGES.noSelection); return false }
        if (rows.length > EXCEL_ROW_LIMIT) { notice.invalid(MESSAGES.excelRowLimitExceeded); return false }
        return true
      },
      /** 삭제 — auth0 전용 + 최대 건수 + 단일 화주(옛 handleDeleteClick). */
      itDeleteButton: (ctx) => {
        if (!isAuth0) { notice.invalid(MESSAGES.deleteNoAuth); return false }
        const rows = pickHeads(ctx)
        if (rows.length > DELETE_MAX_DOCUMENTS) { notice.invalid(MESSAGES.deleteMaxExceeded); return false }
        return guardHeads(ctx)
      },
      itUploadButton: () => true,
      itExcelButton: (ctx) => {
        // 자기 표(하단)의 선택만 본다 — ctx.selected 는 위/아래가 공유하는 값이라 상단 체크가 여기 통과하면 안 된다.
        if (pickItems(ctx).length === 0) { notice.invalid(MESSAGES.noSelection); return false }
        return true
      },
    },
    /** 서치헬퍼 모달 열림 — 후보 조회를 이 신호로 게이트한다(옛 isXxxHelperOpen). */
    onModal: (open) => {
      setHelperOpen((s) =>
        s.stl === open.est01StlSearchModal && s.gds === open.est01GdsSearchModal
          ? s
          : { stl: open.est01StlSearchModal, gds: open.est01GdsSearchModal },
      )
      // 닫히면 그 모달의 적용 조건을 비운다 — 남겨 두면 다시 열 때 뷰가 빈 조건을 주기 전 한 렌더 동안
      //  '열림 + 지난 [조회] 조건'이 되어 후보 요청이 한 번 나간다(#861 리뷰).
      if (!open.est01StlSearchModal && stlQueryRef.current.applied) { stlQueryRef.current = { applied: false, brand: '', stlNo: '' }; setStlQuery({ applied: false, brand: '', stlNo: '' }) }
      if (!open.est01GdsSearchModal && gdsQueryRef.current.applied) { gdsQueryRef.current = { applied: false, brand: '', item: '' }; setGdsQuery({ applied: false, brand: '', item: '' }) }
    },
    /** [초기화] — 남겨 둔 조회 조건도 함께 지운다(조회는 트리거하지 않는다). */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      // 탭바는 reload 로만 새 조건을 읽는다 — 빼면 탭 링크에 지운 조건이 남아 복귀 시 진입 조회가 그 조건으로 돈다.
      menuSession.reload()
      replaceQuery({})
    },
  }

  /*
   * 선택 행 되찾기 — **자기 표의 행만** 돌려준다.
   *  뷰의 ctx.selected 는 위/아래 표가 공유하는 모듈 변수 하나이고 '마지막으로 바뀐 스코프' 만 담는다.
   *  그래서 상단 문서를 체크한 뒤 하단 품목을 체크하면, 상단 [확정]·[삭제] 가 **하단 행**을 받는다.
   *  뷰 행을 그대로 되돌려 쓰면(옛 fallback) 그 품목에서 뽑은 문서번호로 확정·삭제가 나간다 — 틀린 대상이다.
   *  자기 표에서 못 찾으면 onSelect 가 스코프별로 갈라 둔 선택(selectedHeads/selectedItems)을 쓰고,
   *  그것도 없으면 빈 배열 → 기존 noSelection 가드가 막는다.
   */
  function pickHeads(ctx: ActionCtx): ErpDocumentSummary[] {
    // ① 스코프별 선택이 먼저다. HEAD·ITEM 은 **각자 테이블의 PK** 라 id 값이 겹친다(둘 다 1,2,3…) —
    //    ctx.selected 를 id 로만 맞추면 하단 품목 id=1 이 상단 문서 id=1 로 둔갑한다.
    if (selectedHeads.length) {
      const own = new Set(selectedHeads.map((h) => h.id))
      return summaries.filter((s) => own.has(s.id))
    }
    // ② 스코프 신호가 아직 없을 때만(onSelect 이전·테스트) ctx.selected 로 되찾는다.
    //  단, **문서 행일 때만** 쓴다. ctx.selected 는 위아래가 공유하는 값이라 하단 품목이 들어올 수 있는데,
    //  HEAD·ITEM 은 각자 테이블의 PK 라 id 가 겹친다 — 그대로 id 로 맞추면 고르지도 않은 **다른 문서**가
    //  확정·삭제 대상이 된다(2026-09-21 moms-fe #789 봇 리뷰 Critical).
    const heads = ctx.selected.filter((r) => !isItemRow(r))
    if (!heads.length) return []
    const picked = new Set(heads.map((r) => Number((r as { id?: unknown }).id)))
    return summaries.filter((s) => picked.has(s.id))
  }
  /** 선택 품목(하단 표) — pickHeads 와 같은 규약(스코프 우선 → ctx.selected 폴백). */
  function pickItems(ctx: ActionCtx): ErpDocumentItem[] {
    if (selectedItems.length) {
      const own = new Set(selectedItems.map((i) => i.id))
      return items.filter((i) => own.has(i.id))
    }
    // pickHeads 와 대칭 — 폴백은 **품목 행일 때만**(상단 문서가 들어오면 id 가 겹쳐 엉뚱한 품목이 잡힌다).
    const rows = ctx.selected.filter(isItemRow)
    if (!rows.length) return []
    const picked = new Set(rows.map((r) => Number((r as { id?: unknown }).id)))
    return items.filter((i) => picked.has(i.id))
  }
  function guardHeads(ctx: ActionCtx): boolean {
    const rows = pickHeads(ctx)
    if (rows.length === 0) { notice.invalid(MESSAGES.noSelection); return false }
    if (singleShipperOf(rows) === null) { notice.invalid(MESSAGES.mixedShippers); return false }
    return true
  }

  return (
    <>
      <StockTransferConfirmPageView wiring={wiring} />
      {/* 데모에 없는 칸 — 위 est01DeleteConfirm_ok 주석 참조(BE 가 사유를 강제한다). */}
      <DeleteReasonModal
        open={deleteTargets.ordNos.length > 0}
        documentCount={deleteTargets.ordNos.length}
        isDeleting={deleteMutation.isPending}
        onCancel={() => setDeleteTargets({ shipper: '', ordNos: [] })}
        onConfirm={runDelete}
      />
    </>
  )
}

/**
 * 사유 모달 값 — 코드·상세. **여기서 검증까지 한다.**
 *  옛 ExcelDownloadReasonModal 은 `okDisabled` 로 사유코드 필수 + 상세사유 5자 이상을 막았는데,
 *  생성 모달에는 버튼 잠금 슬롯이 없어 [확인]이 항상 눌린다. HEAD 는 BE 가 막아 주지만
 *  **ITEM 은 클라이언트 엑셀이라 BE 가드가 없다** — 빈 사유로 내려받히고 감사로그도 빈 사유로 남는다.
 *  실패는 throw 로 알린다(모달은 열린 채 남는다).
 */
function reasonOf(ctx: ActionCtx): { reason: string; detail: string } {
  const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
  const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
  if (!reason) { throw invalidError(MESSAGES.excelReasonRequired) }
  if (detail.length < EXCEL_REASON_MIN_LENGTH) {
    throw invalidError(MESSAGES.excelReasonDetailTooShort)
  }
  return { reason, detail }
}

/**
 * ITEM 저장 payload — 옛 handleItemSave 와 같은 필드 대응(BE UpdateItem 계약).
 *
 * ⚠ 표에서 편집되는 칸 중 **이동예정일자(PLN_DATE)·지시수량(ORD_QTY)은 저장되지 않는다** —
 *  BE 계약(`UpdateErpDocumentItemReq`)에 그 두 필드가 없다. 화면은 datepicker·수량입력으로 열어 두었지만
 *  값을 고쳐도 조용히 사라진다(2026-09-21 moms-fe #789 리뷰 Critical).
 *  고치려면 **BE 계약 확장이 선행**돼야 한다. 그 전까지는 데모에서 두 칸을 읽기 전용으로 두는 편이 맞다.
 *  TODO(api): UpdateErpDocumentItemReq 에 plannedDate·orderQuantity 추가 → 여기에 실어 보낸다.
 */
function toItemPayload(row: ErpDocumentItem) {
  return {
    shipperCode: row.sprNm ?? '',
    documentNumber: row.ordNo,
    documentSequence: row.ordOptNo ?? '',
    sourceBarcode: row.srcBarcode,
    sourceWarehouseName: row.srcWhNm ?? '',
    sourceWarehouseArea: row.srcWhArea,
    destinationWarehouseName: row.dstWhNm ?? '',
    destinationWarehouseArea: row.dstWhArea,
    receiverName: row.trsNm,
    receiverZipCode: row.trsZip,
    receiverAddress: row.trsAddr,
    receiverAddressBase: row.trsAddrBase,
    receiverAddressDetail: row.trsAddrDetail,
    receiverPhone: row.trsPhone,
    b2bCarrier: row.b2bCarrier,
    etc1: row.etc1,
  }
}

/** ITEM 엑셀 컬럼 — 표에 보이는 것과 같은 순서·헤더(옛 ag-Grid export 와 같은 결과). */
const ITEM_EXCEL_COLUMNS: { key: string; header: string }[] = [
  { key: 'SPR_NM', header: '화주명' },
  { key: 'PLN_DATE', header: '이동예정일자' },
  { key: 'ORD_NO', header: '이동지시문서' },
  { key: 'ORD_OPT_NO', header: '이동지시순번' },
  { key: 'SRC_WH_CD', header: '센터코드(Source)' },
  { key: 'SRC_WH_NM', header: '센터명(Source)' },
  { key: 'SRC_WH_AREA', header: '센터영역(Source)' },
  { key: 'DST_WH_CD', header: '센터코드(Destination)' },
  { key: 'DST_WH_NM', header: '센터명(Destination)' },
  { key: 'DST_WH_AREA', header: '센터영역(Destination)' },
  { key: 'BRAND', header: '브랜드' },
  { key: 'STL_NO', header: '스타일넘버' },
  { key: 'GDS_CD', header: '상품코드' },
  { key: 'GDS_NM', header: '상품명' },
  { key: 'SRC_BARCODE', header: '바코드(Source)' },
  { key: 'ORD_QTY', header: '지시수량' },
  { key: 'B2B_CARRIER', header: '택배사' },
  { key: 'ETC1', header: '비고' },
]

/**
 * 수령자 개인정보 칸 — 다운로드 양식에서 뺀다(1.0 EST01_ITEM 도 COLUMN_EXP=0, LOGISPRD-15798).
 *  업로드는 이 칸이 있는 파일(예전 양식·직접 채운 파일)도 받도록 매핑에만 남긴다.
 * ⚠ 이 칸이 없는 파일로 수정 행을 올리면 receiver* 가 빈 채로 가고, BE 는 도착지가 센터가 아닌 행의
 *  수령자·주소·연락처를 null 로 덮어쓴다(StockTransferErpDocumentCommandService.updateItems). BE 가 null 을 '유지'로 받게 바뀌어야 안전하다.
 */
const RECEIVER_EXCEL_COLUMNS: { key: string; header: string }[] = [
  { key: 'TRS_NM', header: '수령자명' },
  { key: 'TRS_ZIP', header: '우편번호' },
  { key: 'TRS_ADDR', header: '수령지 주소' },
  { key: 'TRS_ADDR_BASE', header: '수령지 주소(기본)' },
  { key: 'TRS_ADDR_DETAIL', header: '수령지 주소(상세)' },
  { key: 'TRS_PHONE', header: '수령자 연락처' },
]

/**
 * 선택 품목 → xlsx 내려받기. 옛 화면의 ag-Grid export 자리를 대신한다(파일명·시트명도 그대로).
 * exceljs 는 레포에 이미 있다 — 새 의존을 들이지 않는다.
 */
async function saveItemsExcel(rows: Record<string, unknown>[]): Promise<void> {
  const ExcelJS = await import('exceljs')
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('재고이동확정')
  ws.addRow(ITEM_EXCEL_COLUMNS.map((c) => c.header))
  for (const r of rows) ws.addRow(ITEM_EXCEL_COLUMNS.map((c) => (r[c.key] ?? '') as string | number))
  const buf = await wb.xlsx.writeBuffer()
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `재고이동확정_품목_${new Date().toISOString().slice(0, 19).replaceAll(/[-:T]/g, '')}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 업로드 파일 → 행 목록. 첫 줄을 헤더로 보고 표 컬럼 키(UPPER_SNAKE)로 읽는다.
 * exceljs 는 레포에 이미 있다(엑셀 생성에 쓰던 것) — 새 의존을 들이지 않는다.
 */
/**
 * 업로드 헤더 → 표 컬럼 키. **다운로드 양식 + 수령자 칸**이다(ITEM_EXCEL_COLUMNS·RECEIVER_EXCEL_COLUMNS 의 header ↔ key 역방향).
 *  이 화면의 업로드는 "내려받아 고쳐 올리는" 흐름이라 파일 첫 줄이 한글 헤더('화주명'·'이동지시문서'…)다.
 *  헤더 문자열을 그대로 키로 쓰면 어떤 키도 API 필드로 안 바뀌어 payload 가 통째로 빈 값이 된다
 *  (2026-09-21 moms-fe #789 리뷰 Critical). UPPER_SNAKE 헤더로 만든 파일도 그대로 받는다.
 */
const UPLOAD_HEADER_TO_KEY: Record<string, string> = Object.fromEntries(
  [...ITEM_EXCEL_COLUMNS, ...RECEIVER_EXCEL_COLUMNS].flatMap((c) => [[c.header, c.key], [c.key, c.key]]),
)

/** 업로드 행에 붙이는 시트 행 번호 키 — 대문자가 아니라 fromViewRow 가 API 키로 바꾸지 않는다. */
const UPLOAD_LINE_KEY = '__line'

/**
 * exceljs 셀 값 → 원시값. 수식·하이퍼링크·서식 텍스트 칸은 객체로 온다 — 그대로 두면 문자열 검사에서 빈 값이 된다.
 */
const cellText = (v: unknown): unknown => {
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  if (v == null || typeof v !== 'object') return v
  const o = v as { result?: unknown; text?: unknown; richText?: { text?: string }[] }
  if ('result' in o) return cellText(o.result)
  if (o.richText) return o.richText.map((t) => t.text ?? '').join('')
  if ('text' in o) return cellText(o.text)
  return v
}

async function readUploadRows(file: File): Promise<Record<string, unknown>[]> {
  const ExcelJS = await import('exceljs')
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) throw new Error('시트를 찾지 못했습니다.')
  const header: string[] = []
  ws.getRow(1).eachCell((cell, col) => { header[col] = String(cell.value ?? '').trim() })
  const rows: Record<string, unknown>[] = []
  ws.eachRow((row, idx) => {
    if (idx === 1) return
    const obj: Record<string, unknown> = {}
    row.eachCell((cell, col) => {
      const key = UPLOAD_HEADER_TO_KEY[header[col] ?? ''] // 한글 헤더·UPPER_SNAKE 둘 다 받는다
      if (key) obj[key] = cellText(cell.value)
    })
    if (Object.keys(obj).length) rows.push({ ...obj, [UPLOAD_LINE_KEY]: idx }) // 빈 줄을 건너뛰므로 안내 줄 번호는 시트 행 번호로
  })
  return rows
}

export default StockTransferConfirm
